import React, { Dispatch, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Card, Accordion, Row, Col } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useFormContext, useWatch, get } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { getNowInPreferredTimezone, getTimezoneOffset, resolvePreferredTimezone } from "helpers/dateTimeHelper";

import "./ProviderForm.scss";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { getDraftProviderOptions } from "api";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import Button from "components/atoms/Button/Button";
import Alert from "components/molecules/Alert/Alert";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import FormSection from "components/v2/FormSection/FormSection";
import getLabel from "helpers/getLabel";
import usePermissions from "hooks/usePermissions";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import { getObjectByKeys } from "helpers/commonHelpers";
import {
  buildAllProviderSelections,
  pruneUnavailableProviders,
  shouldAutoSelectAllProviders,
} from "helpers/requestFormProvider";
import { DefaultProvidersInvitations } from "types/Permissions";
import { trackQuoteProviderSelectAll } from "pages/shipper/RequestForm/formEvents/requestFormAnalytics";
import ProviderSelector from "./ProviderSelector";
import FormItemInfo from "../Common/FormItemInfo";
import ProviderTimingForm from "./ProviderTimingForm";
import ProviderAttachmentForm from "./ProviderAttachmentForm";
import FormHr from "../Common/FormHr";
import useGetMultiDrop from "../Schedule/hooks/useGetMultiDrop";
import useGetMultiStop from "../Schedule/hooks/useGetMultiStop";

type ProviderFormProps = {
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  childRef?: React.MutableRefObject<any>;
  isMiniTender?: boolean;
  validateSection?: Function;
  shouldValidateSection?: React.MutableRefObject<boolean>;
  isNewShipment?: boolean;
  isShipment?: boolean;
  isConsolidated?: boolean;
  handleDraftSave?: () => Promise<void>;
};
function ProviderForm(props: ProviderFormProps) {
  const intl = useIntl();
  const {
    state,
    dispatch,
    childRef,
    isMiniTender,
    validateSection,
    shouldValidateSection,
    isNewShipment,
    isShipment,
    isConsolidated,
    handleDraftSave,
  } = props;

  const { permissions, parsedPermissions } = usePermissions();
  const { default_providers_invitation } = JSON.parse(permissions || "{}").company_shipper_settings;

  const [isFetching, setIsFetching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [missingDataError, setMissingDataError] = useState(false);
  const [bucketAttachments, setBucketAttachments] = useState([]);
  const providerOptionRequestAbortController = useRef<AbortController | null>(null);
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const draftId = searchParams.get("draft_id");
  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { isMultiStopQuoteRequest } = useGetMultiStop();
  const { isMultiDropQuoteRequest } = useGetMultiDrop();
  const pickupDate =
    isMultiDropQuoteRequest || isMultiStopQuoteRequest
      ? useWatch({ name: "schedule.stops.0.stop_after_date", control })
      : useWatch({ name: "schedule.pickup_ready_date", control });

  const location = useWatch({ name: "organizational.location", control });
  const invitedModes = useWatch({ name: "invited_modes", control });
  const selectedProviders = useWatch({ name: "invited_providers.providers", control }) || [];
  const providerFormFields = FormStepValidationTypes[FormStepTypes.PROVIDER] as string[];
  const hasError = providerFormFields.some((item) => get(errors, item));

  useImperativeHandle(childRef, () => ({ checkStepValidation, refetchProviders }));
  const urlOrigin = window.location.origin;

  const activeAndRequiredBucketAttachments: any = useMemo(
    () => (bucketAttachments || []).filter((item: any) => item.is_active && item.is_mandatory),
    [bucketAttachments],
  );

  const bucketAttachmentHasMap: any = useMemo(
    () => getObjectByKeys(getValues("bucket_attachments") || []),
    [getValues("bucket_attachments")],
  );

  const filteredOptions = useMemo(() => {
    if (searchText?.trim()?.length && state.providers.invited_providers?.length && !isNewShipment) {
      return state.providers.invited_providers?.filter((option: any) =>
        option.company_name.toLowerCase().includes(searchText.toLowerCase()),
      );
    }
    /** filter provider list on the basis of is_parcel_company and rate_card_available  for new shipment */
    if (isNewShipment) {
      return state.providers.invited_providers?.filter(
        (option: any) => option.rate_card_available === true || option.is_parcel_company === true,
      );
    }
    return state.providers.invited_providers;
  }, [searchText, state.providers.invited_providers]);

  const selectedFreightModes = useMemo(() => {
    let freightModes: any = [];
    if (invitedModes && Object.keys(invitedModes).length) {
      freightModes =
        Object.keys(invitedModes).filter(
          (key) => typeof invitedModes[key] === "boolean" && invitedModes[key] && !key.includes("custom"),
        ) || [];
      if (invitedModes.custom && invitedModes.custom.length) {
        invitedModes.custom.forEach((item: any) => {
          if (item.value) {
            freightModes.push(`custom_${item.key}`);
          }
        });
      }
    }
    return freightModes;
  }, [invitedModes]);

  useEffect(() => {
    let handler: NodeJS.Timeout;
    if (selectedFreightModes.length && draftId) {
      handler = setTimeout(() => {
        getProviders(selectedFreightModes, draftId);
      }, 1000);
    }
    return () => {
      if (handler) {
        clearTimeout(handler);
      }
    };
  }, [selectedFreightModes, draftId, state.draftSaved[FormStepTypes.ORGANISATION]]);

  useEffect(() => {
    if (currentActiveStep === FormStepTypes.PROVIDER) {
      refetchProviders();
    }
  }, [currentActiveStep]);

  const getProviders = (selectedFreights: string[], draftShipmentId: string) => {
    if (!state.providers?.invited_providers?.length) {
      setIsFetching(true);
    }
    if (providerOptionRequestAbortController.current?.abort) {
      providerOptionRequestAbortController.current.abort();
    }
    providerOptionRequestAbortController.current = new AbortController();
    getDraftProviderOptions(draftShipmentId, {
      params: { freight_modes: selectedFreights.join(",") },
      signal: providerOptionRequestAbortController.current.signal,
      timeout: 300000,
    })
      .then((res) => {
        setMissingDataError(false);
        const updatedData = { ...res.data };
        if (updatedData?.invited_providers?.length) {
          updatedData.invited_providers = updatedData.invited_providers.map((item: any) => {
            if (item.parcel_accounts && Array.isArray(item.parcel_accounts) && item.parcel_accounts.length) {
              return {
                ...item,
                parcel_accounts: item.parcel_accounts.map((data: any) => ({
                  ...data,
                  label: `${data.account_name} - ${data.account_number}`,
                  value: data.id,
                })),
              };
            }
            return item;
          });
        }
        dispatch({ type: RequestFormActionType.PROVIDER_REQUEST_SUCCESS, payload: updatedData });
        removeUnavailableProviders(res?.data?.invited_providers || []);
        checkAndSelectAllByDefault(res?.data);
        if (shouldValidateSection && validateSection && shouldValidateSection.current) {
          setTimeout(() => {
            validateSection(FormStepTypes.PROVIDER);
          }, 200);
        }
        setIsFetching(false);
      })
      .catch((err) => {
        if (err.name === "CanceledError") {
          return;
        }
        if (err?.response?.data?.[0]?.includes("Missing organizational data")) {
          setMissingDataError(true);
        }
        setIsFetching(false);
      });
  };

  const checkAndSelectAllByDefault = (data: any) => {
    const invitedProviders = getValues("invited_providers.providers") ?? [];
    const providerOptions = data?.invited_providers ?? [];

    if (
      !shouldAutoSelectAllProviders(
        default_providers_invitation,
        invitedProviders.length,
        data?.minimum_invites_possible ?? 0,
        providerOptions.length,
      )
    ) {
      return;
    }

    const { providers, parcel_accounts } = buildAllProviderSelections(providerOptions);
    setValue("invited_providers.providers", providers);
    setValue("invited_providers.parcel_accounts", parcel_accounts);
    clearErrors("invited_providers");
  };

  const refetchProviders = () => {
    if (selectedFreightModes.length && draftId) {
      getProviders(selectedFreightModes, draftId);
    }
  };

  const checkStepValidation = () => {
    let shouldInvalidate = true;
    const invitedProviders = getValues("invited_providers.providers");

    if (invitedProviders?.length && invitedProviders.length >= state.providers?.minimum_invites_possible) {
      shouldInvalidate = false;
    }

    if (invitedProviders?.length >= 1 && isNewShipment) {
      shouldInvalidate = false;
    }

    if (!selectedFreightModes?.length && location) {
      setError("invited_providers", {
        type: "custom",
      });
      return false;
    }

    if (!pickupDate && location && !isMiniTender && !isNewShipment) {
      setError("shipment.quoting_deadline_date", {
        type: "custom",
      });
      return false;
    }

    if (shouldInvalidate && state.providers?.minimum_invites_possible && !isNewShipment) {
      setError("invited_providers", {
        type: "custom",
        message: `Please select at least ${state.providers?.minimum_invites_possible} providers`,
      });
      return false;
    }
    if (shouldInvalidate && (isNewShipment || (!invitedProviders?.length && !state.providers?.minimum_invites_possible))) {
      setError("invited_providers", {
        type: "custom",
        message: `Please select at least 1 providers`,
      });
      return false;
    }
    if (
      activeAndRequiredBucketAttachments.length &&
      (!getValues("bucket_attachments")?.length ||
        !activeAndRequiredBucketAttachments.every((item: any) => bucketAttachmentHasMap[item.id]))
    ) {
      setError("bucket_attachments", {
        type: "custom",
      });
      return false;
    }
    return true;
  };

  /**
   * remove those selected providers who are not available
   * anymore due to change in selected freight modes
   */
  const removeUnavailableProviders = (availableProviders: Array<{ id: number; provider_reference?: string }>) => {
    const currentSelectedProviders = getValues("invited_providers.providers") || [];
    const parcelAccounts = getValues("invited_providers.parcel_accounts") || [];
    const { providers, parcel_accounts, changed } = pruneUnavailableProviders(
      currentSelectedProviders,
      parcelAccounts,
      availableProviders,
    );

    if (changed) {
      setValue("invited_providers.providers", providers);
      setValue("invited_providers.parcel_accounts", parcel_accounts);
    }
  };

  const handleSelectAll = (invitedProviders = []) => {
    const providers = state.providers?.invited_providers || invitedProviders;
    const { providers: allSelectedProviders, parcel_accounts: allSelectedParcelAccounts } = buildAllProviderSelections(
      providers ?? [],
    );
    setValue("invited_providers.providers", allSelectedProviders);
    setValue("invited_providers.parcel_accounts", allSelectedParcelAccounts);
    clearErrors("invited_providers");
    trackQuoteProviderSelectAll((allSelectedProviders?.length || 0) + (allSelectedParcelAccounts?.length || 0));
  };

  const handleDeselectAll = () => {
    setValue("invited_providers.providers", []);
    setValue("invited_providers.parcel_accounts", []);
  };

  useEffect(() => {
    if (isNewShipment) {
      const quotingDeadline = getNowInPreferredTimezone(parsedPermissions?.preferred_timezone).add(2, "days");
      const confirmationDeadline = getNowInPreferredTimezone(parsedPermissions?.preferred_timezone).add(2, "hours");
      setValue(`shipment.quoting_deadline_date`, quotingDeadline.format("YYYY-MM-DD"));
      setValue(`shipment.quoting_deadline_time`, quotingDeadline.format("HH:mm:ss"));
      setValue(`shipment.confirmation_date`, quotingDeadline.format("YYYY-MM-DD"));
      setValue(`shipment.confirmation_time`, confirmationDeadline.format("HH:mm:ss"));
    }
  }, [isNewShipment, parsedPermissions?.preferred_timezone, setValue]);

  const renderProviderSection = () => (
    <div className="provider-form">
      {isFetching && (
        <Row>
          <Col className="pl-4">
            <div className="skeleton-card">
              <div>
                <Row className="skeleton-row">
                  <span key={1} className="skeleton" />
                  <span key={2} className="skeleton" />
                  <span key={3} className="skeleton" />
                  <span key={4} className="skeleton" />
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      )}
      {!isFetching && location && (
        <div>
          {!isShipment && (
            <>
              <div className="flex-row">
                <div className="w-200 mr-xl">
                  <FormItemInfo
                    labelId="new-request.provider.providerSelection"
                    label="Provider selection"
                    descId="new-request.provider.providerSelectionInfo"
                    description="Choose the providers you want to invite to quote"
                  />
                </div>
                <div>
                  {!!state.providers.invited_providers?.length && !!selectedFreightModes?.length && !missingDataError && (
                    <>
                      <div className="flex-row mb-4">
                        <div className="d-flex align-items-center" style={{ height: "26px" }}>
                          <div data-testid={testIds.REQUEST_FORM_PROVIDER_SEARCH}>
                            <ExpandableSearch
                              placeholder="Search provider"
                              containerClasses="mr-3"
                              alwaysExpanded
                              value={searchText}
                              onChange={(e) => setSearchText(e.target.value)}
                            />
                          </div>
                          {filteredOptions?.length > 1 && (
                            <>
                              {selectedProviders?.length === state.providers?.invited_providers?.length ? (
                                <Button
                                  variant="primary"
                                  className="rounded-pill blue-200 h-26"
                                  data-testid={testIds.REQUEST_FORM_PROVIDER_DESELECT_ALL_BUTTON}
                                  label={intl.formatMessage({
                                    id: "new-request.provider.deselectAll",
                                    defaultMessage: "Deselect all",
                                  })}
                                  onClick={handleDeselectAll}
                                  disabled={
                                    (state.providers?.minimum_invites_possible > 0 &&
                                      state.providers?.minimum_invites_possible ===
                                        state.providers?.invited_providers?.length) ||
                                    default_providers_invitation ===
                                      DefaultProvidersInvitations.PROVIDERS_INVITATION_ALL_MANDATORY
                                  }
                                />
                              ) : (
                                <Button
                                  variant="primary"
                                  className="rounded-pill blue-200 h-26"
                                  label="Select all"
                                  data-testid={testIds.REQUEST_FORM_PROVIDER_SELECT_ALL_BUTTON}
                                  onClick={() => handleSelectAll()}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="provider-selection-grid">
                        {filteredOptions.map((provider: any) => (
                          <div className="w-192" key={provider.id}>
                            <ProviderSelector providerData={provider} state={state} isNewShipment={isNewShipment} />
                          </div>
                        ))}
                      </div>
                      {!!searchText.length && !filteredOptions.length && (
                        <div className="flex-row">
                          <div>
                            <p>No results matching {searchText}</p>
                          </div>
                        </div>
                      )}
                      {!filteredOptions?.length && isNewShipment && (
                        <Row className="mr-26">
                          <Alert variant="warning" className=" ml-3 no-wrap">
                            <span>
                              <FormattedMessage
                                id="quote-request.quotationProviderNotFound"
                                defaultMessage="There are not rate card or parcel providers available. Want to do a full quote request,click here"
                              />{" "}
                            </span>
                            <Link to={`/quote-request?active_step=provider&draft_id=${draftId}`}>
                              {urlOrigin}/quote-request?active_step=provider&draft_id={draftId}
                            </Link>
                          </Alert>
                        </Row>
                      )}
                    </>
                  )}

                  {!selectedFreightModes?.length && !missingDataError && (
                    <div>
                      <Alert variant="warning">
                        <FormattedMessage
                          id="new-request.provider.selectAFreightMode"
                          defaultMessage="Please select a freight mode"
                        />
                      </Alert>
                    </div>
                  )}
                  {missingDataError && (
                    <div>
                      <Alert variant="warning">
                        <FormattedMessage
                          id="new-request.provider.completeOrganisationStepFirst"
                          defaultMessage="Please complete organisation step first"
                        />
                      </Alert>
                    </div>
                  )}
                  {!!selectedFreightModes?.length && !missingDataError && !state.providers?.invited_providers?.length && (
                    <Row>
                      <Col md={12}>
                        <Alert variant="warning" className=" ml-3 no-wrap">
                          {isNewShipment ? (
                            <p>
                              <span>
                                <FormattedMessage
                                  id="quote-request.quotationProviderNotFound"
                                  defaultMessage="There are not rate card or parcel providers available. Want to do a full quote request,click here"
                                />{" "}
                              </span>
                              <Link to={`/quote-request?active_step=provider&draft_id=${draftId}`}>
                                {urlOrigin}/quote-request?active_step=provider&draft_id={draftId}
                              </Link>
                            </p>
                          ) : (
                            <p>
                              <FormattedMessage
                                id="new-request.provider.noProvidersAvailable"
                                defaultMessage="No providers available"
                              />
                            </p>
                          )}
                        </Alert>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    {get(errors, "invited_providers") && (
                      <div className="d-block font-b3 invalid-feedback ml-4">
                        {get(errors, "invited_providers")?.message && get(errors, "invited_providers").message}
                      </div>
                    )}
                  </Row>
                </div>
              </div>
              <FormHr />
              {!isNewShipment && (
                <>
                  <div className="flex-row mt-24 mb-24">
                    <div className="w-200 mr-xl">
                      <FormItemInfo
                        labelId="new-request.provider.timimg"
                        label="Timing"
                        descId="new-request.provider.timingInfo"
                        description="Set deadlines for providers"
                      />
                      <div className="text-secondary font-b3">
                        {getTimezoneOffset(resolvePreferredTimezone(parsedPermissions?.preferred_timezone))}
                      </div>
                    </div>
                    <div>
                      <ProviderTimingForm isMiniTender={isMiniTender} />
                    </div>
                  </div>
                  <FormHr />
                </>
              )}
            </>
          )}
          <ProviderAttachmentForm setBucketAttachments={setBucketAttachments} handleDraftSave={handleDraftSave} />
        </div>
      )}

      {!isFetching && !location && (
        <Alert variant="warning" className="mb-3 ml-3">
          <FormattedMessage
            id="new-request.organisational-details.selectPaymentLocationPrompt"
            defaultMessage="Please select a Payment location before continuing"
            values={{ paymentLocation: getLabel("Payment location", "capitalize") }}
          />
        </Alert>
      )}
    </div>
  );

  if (isConsolidated) {
    return (
      <div className="mt-xl">
        <FormSection>
          <FormHeader title="Provider" />
          <div className="form-section-content">{renderProviderSection()}</div>
        </FormSection>
      </div>
    );
  }

  return (
    <Card className="mb-3 form-container provider-form" data-testid={testIds.REQUEST_FORM_PROVIDER_SECTION}>
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.PROVIDER}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[FormStepTypes.PROVIDER]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="general.provider" defaultMessage="Provider" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.PROVIDER ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.PROVIDER}>
        <div className="p-xl">{renderProviderSection()}</div>
      </Accordion.Collapse>
    </Card>
  );
}

export default ProviderForm;

ProviderForm.defaultProps = {
  isMiniTender: false,
  validateSection: undefined,
  shouldValidateSection: undefined,
  isNewShipment: false,
  isShipment: false,
  isConsolidated: false,
  childRef: undefined,
  handleDraftSave: undefined,
};
